import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit2, Trash2, Save, X, Eye, EyeOff, Lock, User, FileText, Download, Calendar, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementPanel = ({ token, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', mobile: '', password: '', role: 'admin' });
  const [expandedUser, setExpandedUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sortedUsers = response.data.sort((a, b) => {
        if (a.is_primary) return -1;
        if (b.is_primary) return 1;
        return 0;
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserProfile = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserProfile(null);
      return;
    }
    setExpandedUser(userId);
    setLoadingProfile(true);
    try {
      const resp = await axios.get(`${API}/admin/users/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserProfile(resp.data);
    } catch (e) {
      console.error('Error fetching user profile:', e);
      setUserProfile(null);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.name.trim()) { setMessage({ type: 'error', text: 'Name is required' }); return; }
    if (!newUser.email.trim()) { setMessage({ type: 'error', text: 'Email is required' }); return; }
    if (!newUser.mobile.trim()) { setMessage({ type: 'error', text: 'Phone is required' }); return; }
    if (!newUser.password || newUser.password.length < 8) { setMessage({ type: 'error', text: 'Password must be at least 8 characters' }); return; }
    if (!/\S+@\S+\.\S+/.test(newUser.email)) { setMessage({ type: 'error', text: 'Invalid email format' }); return; }
    try {
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'User created successfully' });
      setShowAddUser(false);
      setNewUser({ name: '', email: '', mobile: '', password: '', role: 'admin' });
      fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create user' });
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      const updateData = {};
      if (editingUser.name) updateData.name = editingUser.name;
      if (editingUser.email) updateData.email = editingUser.email;
      if (editingUser.mobile) updateData.mobile = editingUser.mobile;
      if (editingUser.password) updateData.password = editingUser.password;
      if (editingUser.role) updateData.role = editingUser.role;
      
      await axios.put(`${API}/admin/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'User updated successfully' });
      setEditingUser(null);
      setShowEditPassword(false);
      fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to update user' });
    }
  };

  const handleDeleteUser = async (userId, isPrimary) => {
    if (isPrimary) {
      setMessage({ type: 'error', text: 'Cannot delete primary admin account' });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'User deleted successfully' });
      fetchUsers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to delete user' });
    }
  };

  return (
    <div className="user-management-section">
      <div className="section-header">
        <h3><Users size={18} /> User Management</h3>
        <Button onClick={() => setShowAddUser(!showAddUser)} className="add-user-btn" data-testid="add-user-btn">
          <Plus size={16} /> Add User
        </Button>
      </div>

      {message.text && (
        <div className={`settings-message ${message.type}`}>{message.text}</div>
      )}

      {showAddUser && (
        <form onSubmit={handleAddUser} className="user-mgmt-form">
          <div className="user-mgmt-grid">
            <div className="user-mgmt-field">
              <label>Name</label>
              <Input placeholder="Full Name" value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} required data-testid="new-user-name" />
            </div>
            <div className="user-mgmt-field">
              <label>Email</label>
              <Input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required data-testid="new-user-email" autoComplete="new-email" />
            </div>
            <div className="user-mgmt-field">
              <label>Phone</label>
              <Input type="tel" placeholder="+91 XXXXX XXXXX" value={newUser.mobile} onChange={(e) => setNewUser({...newUser, mobile: e.target.value})} required data-testid="new-user-phone" />
            </div>
            <div className="user-mgmt-field">
              <label>Role</label>
              <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="user-mgmt-select" data-testid="new-user-role">
                <option value="admin">Admin</option>
                <option value="staff">Staff</option>
                <option value="partner">Partner</option>
                <option value="client">Client</option>
              </select>
            </div>
            <div className="user-mgmt-field">
              <label>Password</label>
              <div className="password-field-wrapper">
                <Input type={showNewUserPassword ? 'text' : 'password'} placeholder="Min 8 characters" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required minLength={8} data-testid="new-user-password" autoComplete="new-password" />
                <button type="button" className="password-field-toggle" onClick={() => setShowNewUserPassword(!showNewUserPassword)}>
                  {showNewUserPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <Button type="submit" className="save-btn"><Save size={14} /> Create User</Button>
            <Button type="button" onClick={() => setShowAddUser(false)} className="cancel-btn"><X size={14} /> Cancel</Button>
          </div>
        </form>
      )}

      <div className="users-list">
        {loading ? (
          <p>Loading users...</p>
        ) : users.length === 0 ? (
          <p>No users found</p>
        ) : (
          users.map((user) => (
            <div key={user.id || user.username} className={`user-item ${user.is_primary ? 'primary-user' : ''}`}>
              {editingUser?.id === user.id ? (
                <div className="user-mgmt-form" style={{ margin: 0, padding: '1rem' }}>
                  <div className="user-mgmt-grid">
                    <div className="user-mgmt-field">
                      <label>Name</label>
                      <Input value={editingUser.name || ''} onChange={(e) => setEditingUser({...editingUser, name: e.target.value})} data-testid={`edit-name-${user.id}`} />
                    </div>
                    <div className="user-mgmt-field">
                      <label>Email</label>
                      <Input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser({...editingUser, email: e.target.value})} data-testid={`edit-email-${user.id}`} />
                    </div>
                    <div className="user-mgmt-field">
                      <label>Phone</label>
                      <Input type="tel" value={editingUser.mobile || ''} onChange={(e) => setEditingUser({...editingUser, mobile: e.target.value})} data-testid={`edit-phone-${user.id}`} />
                    </div>
                    <div className="user-mgmt-field">
                      <label>Role</label>
                      {user.is_primary ? (
                        <span style={{ padding: '0.5rem 0', color: '#4a5568', fontWeight: 500, textTransform: 'capitalize' }}>{user.role} (Primary)</span>
                      ) : (
                        <select value={editingUser.role || user.role} onChange={(e) => setEditingUser({...editingUser, role: e.target.value})} className="user-mgmt-select" data-testid={`edit-role-${user.id}`}>
                          <option value="admin">Admin</option>
                          <option value="staff">Staff</option>
                          <option value="partner">Partner</option>
                          <option value="client">Client</option>
                        </select>
                      )}
                    </div>
                    <div className="user-mgmt-field">
                      <label>Change Password</label>
                      <div className="password-field-wrapper">
                        <Input type={showEditPassword ? 'text' : 'password'} value={editingUser.password || ''} onChange={(e) => setEditingUser({...editingUser, password: e.target.value})} placeholder="Leave blank to keep" data-testid={`edit-password-${user.id}`} />
                        <button type="button" className="password-field-toggle" onClick={() => setShowEditPassword(!showEditPassword)}>
                          {showEditPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                    <Button onClick={() => handleUpdateUser(user.id)} className="save-btn"><Save size={14} /> Save</Button>
                    <Button onClick={() => { setEditingUser(null); setShowEditPassword(false); }} className="cancel-btn"><X size={14} /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="user-info">
                    {user.is_primary && <span className="primary-badge">Primary</span>}
                    <span className="user-name">{user.name || user.username}</span>
                    <span className="user-email">{user.email}</span>
                    {user.mobile && <span className="user-mobile"><Phone size={12} /> {user.mobile}</span>}
                    <span className={`user-role-badge ${user.role}`}>{user.role || 'admin'}</span>
                  </div>
                  <div className="user-actions">
                    <Button onClick={() => toggleUserProfile(user.id)} className={expandedUser === user.id ? 'edit-btn-sm active' : 'edit-btn-sm'} title="View Profile" data-testid={`view-profile-${user.id}`}>
                      <User size={14} />
                    </Button>
                    <Button onClick={() => setEditingUser({id: user.id, name: user.name, email: user.email, mobile: user.mobile, role: user.role})} className="edit-btn-sm" data-testid={`edit-user-${user.id}`}>
                      <Edit2 size={14} />
                    </Button>
                    {!user.is_primary && currentUser?.role !== 'staff' && (
                      <Button onClick={() => handleDeleteUser(user.id, user.is_primary)} className="delete-btn-sm" data-testid={`delete-user-${user.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </>
              )}
              {/* Expandable Profile Section */}
              {expandedUser === user.id && (
                <div className="user-profile-expanded" data-testid={`user-profile-expanded-${user.id}`}>
                  {loadingProfile ? (
                    <p style={{ padding: '1rem', color: '#718096', fontSize: '0.9rem' }}>Loading profile...</p>
                  ) : userProfile ? (
                    <>
                      <div className="user-profile-grid">
                        <div className="profile-detail">
                          <span className="detail-label">Birthday</span>
                          <span className="detail-value">{userProfile.profile?.birthday || 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Anniversary</span>
                          <span className="detail-value">{userProfile.profile?.anniversary || 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Aadhar Number</span>
                          <span className="detail-value">{userProfile.profile?.aadhar_number ? `****${userProfile.profile.aadhar_number.slice(-4)}` : 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">PAN Number</span>
                          <span className="detail-value">{userProfile.profile?.pan_number ? `****${userProfile.profile.pan_number.slice(-4)}` : 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Passport Number</span>
                          <span className="detail-value">{userProfile.profile?.passport_number ? `****${userProfile.profile.passport_number.slice(-4)}` : 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Passport Expiry</span>
                          <span className="detail-value">{userProfile.profile?.passport_expiry || 'Not set'}</span>
                        </div>
                        <div className="profile-detail">
                          <span className="detail-label">Member Since</span>
                          <span className="detail-value">{userProfile.created_at ? new Date(userProfile.created_at).toLocaleDateString('en-GB') : 'N/A'}</span>
                        </div>
                      </div>
                      {/* User Documents */}
                      <div className="user-docs-section">
                        <h4 style={{ fontSize: '0.95rem', color: '#2d3748', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FileText size={16} /> Uploaded Documents ({(userProfile.profile?.documents || []).length})</h4>
                        {(userProfile.profile?.documents || []).length === 0 ? (
                          <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>No documents uploaded.</p>
                        ) : (
                          <div className="user-docs-list">
                            {(userProfile.profile?.documents || []).map((doc) => (
                              <div key={doc.id} className="user-doc-item">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <FileText size={16} />
                                  <div>
                                    <span style={{ fontWeight: 500, fontSize: '0.9rem', display: 'block' }}>{doc.name || 'Document'}</span>
                                    <span style={{ fontSize: '0.8rem', color: '#718096', textTransform: 'capitalize' }}>{doc.doc_type || 'other'}</span>
                                  </div>
                                </div>
                                <a href={`${API}/${doc.url?.replace('/api/', '')}`} target="_blank" rel="noopener noreferrer" className="view-doc-btn" data-testid={`admin-view-user-doc-${doc.id}`}>
                                  <Download size={14} /> Download
                                </a>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <p style={{ padding: '1rem', color: '#e53e3e', fontSize: '0.9rem' }}>Failed to load profile</p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Storage Capacity Panel Component

export { UserManagementPanel };
